<template>
  <v-sheet
    ref="homeVideo"
    class="homeVideo"
    :class="{ ['homeVideo--loading']: loading }"
  >
    <!-- Test Mode -->
    <template v-if="testMode"> </template>
    <test-kv-web-rtc v-if="false" />
    <!-- End Test Mode -->
    <template v-else>
      <section class="homeVideo_videos-wrapper">
        <div
          class="homeVideo_videos"
          :class="{ ['homeVideo_videos--loading']: loading }"
        >
          <home-skeleton v-if="loading" />

          <trim-transition
            v-if="transitionImages[mainCams[0]]"
            :left="15"
            :tx="transitionImages[mainCams[0]].tx"
            :ty="transitionImages[mainCams[0]].ty"
            :width="transitionImages[mainCams[0]].width"
            class="homeVideo_trim-transition"
          >
            <img
              v-if="transitionImages[mainCams[0]].show"
              key="img-trans-trim"
              :src="transitionImages[mainCams[0]].src"
              alt=""
            />
          </trim-transition>
          <draggable
            v-model="mainCams"
            :sort="false"
            :move="handleMove"
            group="cams"
            class="homeVideo_main"
            :class="{ homeVideo_drop: movedTo === 'main' }"
            handle=".videoStream_drag-action"
            @end="handleDragEnd"
            @start="handleDragStart"
          >
            <paginate
              v-show="!loading"
              ref="paginate"
              v-model="page"
              :page-count="pageCount"
              :page-range="10"
              :max="15"
              :margin-pages="2"
              :prev-text="'Prev'"
              :next-text="'Next'"
              container-class="homeVideo_paginate"
              :page-class="'page-item'"
              @onPage="handlePage"
            />
            <template v-if="!loading">
              <span
                v-ripple
                class="homeVideo_nextcam"
                @click="$refs.paginate.nextPage()"
              >
                <v-icon size="22"> mdi-chevron-right </v-icon></span
              >
              <span
                v-ripple
                class="homeVideo_prevcam"
                @click="$refs.paginate.prevPage()"
              >
                <v-icon size="22"> mdi-chevron-right </v-icon></span
              >
              <span
                v-if="
                  trimMovePoint !== null && trimMode && mainFrameState().trim
                "
                class="homeVideo_title-trim"
                >{{
                  ['left', 'root'].includes(trimMovePoint) ? '開始 ' : '終了 '
                }}
                {{
                  (['left', 'root'].includes(trimMovePoint)
                    ? trimStart
                    : trimEnd) | dateMs('HH：mm：ss', 'unix')
                }}</span
              >
              <span
                v-if="
                  showEvent &&
                  cameraById[mainCams[0]] &&
                  cameraStatus[mainCams[0]] === 'connected' &&
                  !!cameraActiveEvents[mainCams[0]] &&
                  cameraActiveEvents[mainCams[0]].title &&
                  !loading
                "
                class="homeVideo_title_type"
                :style="
                  getEventLabelStyle(cameraActiveEvents[mainCams[0]].title)
                "
                >{{ cameraActiveEvents[mainCams[0]].title }}</span
              >
            </template>
            <div @dragover="setDraggingOverIndex(mainDragIndex)">
              <video-stream
                v-if="
                  !loading &&
                  cameraById[mainCams[0]] &&
                  cameraById[mainCams[0]].id &&
                  showVideoStream
                "
                :id="cameraById[mainCams[0]].id"
                :key="`main-cam${mainCams[0]}`"
                :ref="`display-cam${mainCams[0]}`"
                :meta="mainCam"
                :default-live-mode="
                  getCameraLiveMode(cameraById[mainCams[0]].id)
                "
                :show-about="showCameraAbout"
                :show-auto-focus="showAutoFocus"
                class="homeVideo_videoStream"
                controller-size="small"
                :stream-config="{
                  accessKeyId: iamKey,
                  secretAccessKey: iamSecret,
                  streamName: cameraById[mainCams[0]].kvsVideoStreamName,
                  region: cameraById[mainCams[0]].awsRegionName,
                  startTimestamp: startTimestamp,
                  endTimestamp: endTimestamp,
                  autoplay: canAutoplay,
                }"
                :title="getCameraTitle(mainCams[0])"
                :hide-display-controller="captureMode || trimMode"
                :class="
                  !fullscreen && cameraStatus[mainCams[0]] === 'connected'
                    ? {
                        'homeVideo_main-event': mainFrameState().event,
                        homeVideo_hide: draggingOverIndex === mainDragIndex,
                        'homeVideo_selected-capture': mainFrameState().capture,
                        'homeVideo_selected-trim': mainFrameState().trim,
                        homeVideo_unselected: mainFrameState().unselected,
                      }
                    : {}
                "
                :main="true"
                :event="[cameraActiveEvents[mainCams[0]]]"
                :live.sync="live"
                :playing.sync="playing"
                :playback-rate.sync="playbackRate"
                :draggable="isCurrentAvatarOwner"
                :bottom-controller="{
                  mode: captureMode || trimMode ? 'select' : 'controller',
                  selectMode: captureMode
                    ? 'capture'
                    : trimMode
                    ? 'trim'
                    : 'none',
                  selected: camSelectItems[0]
                    ? camSelectItems[0].selected
                    : false,
                }"
                @onAction="handleVideoStreamAction"
                @onMouseover="handleVideoMouseover"
                @onMouseLeave="handleVideoMouseleave"
                @timeupdate="handleVideoStreamTimeUpdate($event, mainCams[0])"
                @onCameraStatus="handleCamStatus"
                @onFullscreenChange="handleFullscreenChange"
                @onSelect="handleSelectCamera($event, 0)"
                @onLiveMode="handleLiveMode($event, true)"
                @onWaiting="handleWaitingVideoData"
                @onSeeking="handleSeeking"
                @onLoading="handleCameraLoading"
              >
                <template slot-scope="{ show, _class }">
                  <VideoSeek
                    name="HomeVideo-main"
                    :show-playback-rate="isAnyCamConnected"
                    :v-show="show"
                    fullscreen-mode
                    :class="_class"
                    :can-edit-date-time="canEditVideoSeekDateTime"
                    :live.sync="live"
                    :playing.sync="playing"
                    :playback-rate.sync="playbackRate"
                    :timestamp="
                      currentLiveMode === 'WebRTC' && live
                        ? dateTimeNow
                        : timestamp
                    "
                    :max-days="maxRetentionPeriodDays"
                    :time-range="currentTimeRange"
                    @setTime="getTime"
                    @onLive="handleLive"
                    @changeCurrentTime="handleChangeCurrentTime"
                    @onGetDate="handleGetDate"
                    @onSetTimeRange="handleSetTimeRange"
                  />
                </template>
              </video-stream>
            </div>
          </draggable>
          <draggable
            ref="secondaries"
            v-model="secondaryCams"
            :sort="false"
            class="homeVideo_secondaries"
            :move="handleMove"
            group="cams"
            handle=".videoStream_drag-action"
            @end="handleDragEnd"
          >
            <!--
              NOTE: secondaryIndex+1 = camera position starts after main camera for detecting selected camera style.
              For dragging the index would be based on `secondaryCams` array index so there is no change
             -->
            <template v-for="(cameraId, secondaryIndex) in secondaryCams">
              <div
                :key="secondaryIndex"
                class="homeVideo_secondary"
                :class="{
                  homeVideo_drop:
                    movedTo === 'secondary' &&
                    draggingOverIndex === secondaryIndex,
                }"
                @dragover="setDraggingOverIndex(secondaryIndex)"
              >
                <span
                  v-if="
                    trimMovePoint !== null &&
                    trimMode &&
                    secondaryFrameState(cameraId, secondaryIndex + 1).trim
                  "
                  class="homeVideo_title-trim homeVideo_title-trim--secondary"
                  >{{
                    ['left', 'root'].includes(trimMovePoint) ? '開始 ' : '終了 '
                  }}
                  {{
                    (['left', 'root'].includes(trimMovePoint)
                      ? trimStart
                      : trimEnd) | dateMs('HH：mm：ss', 'unix')
                  }}</span
                >
                <span
                  v-if="
                    showEvent &&
                    cameraStatus[cameraId] === 'connected' &&
                    !!cameraActiveEvents[cameraId] &&
                    cameraActiveEvents[cameraId].title &&
                    !loading
                  "
                  class="homeVideo_secondary-title"
                  :style="
                    getEventLabelStyle(cameraActiveEvents[cameraId].title)
                  "
                  >{{ cameraActiveEvents[cameraId].title }}</span
                >
                <trim-transition
                  v-if="transitionImages[cameraId]"
                  :tx="transitionImages[cameraId].tx"
                  :ty="transitionImages[cameraId].ty"
                  :width="transitionImages[cameraId].width"
                  class="homeVideo_trim-transition"
                >
                  <img
                    v-if="transitionImages[cameraId].show"
                    key="img-trans-trim"
                    :src="transitionImages[cameraId].src"
                    alt=""
                  />
                </trim-transition>
                <video-stream
                  v-if="!loading && showVideoStream && cameraById[cameraId]"
                  :id="cameraId"
                  :key="`secondary-cam${cameraId}`"
                  :ref="`display-cam${cameraId}`"
                  :meta="secondariesCam[secondaryIndex]"
                  :default-live-mode="getCameraLiveMode(cameraId)"
                  :hide-zoom="!fullscreen"
                  :disable-zoom="!fullscreen"
                  :stream-config="{
                    accessKeyId: iamKey,
                    secretAccessKey: iamSecret,
                    streamName: cameraById[cameraId].kvsVideoStreamName,
                    region: cameraById[cameraId].awsRegionName,
                    startTimestamp: startTimestamp,
                    endTimestamp: endTimestamp,
                    autoplay: canAutoplay,
                  }"
                  :show-about="showCameraAbout"
                  :show-auto-focus="showAutoFocus"
                  :hide-tools="true"
                  :title="getCameraTitle(cameraId)"
                  :set-time="setTime"
                  :main="false"
                  :event="[cameraActiveEvents[cameraId]]"
                  :live.sync="live"
                  :playing.sync="playing"
                  :playback-rate.sync="playbackRate"
                  :hide-display-controller="captureMode || trimMode"
                  :class="
                    !fullscreen && cameraStatus[cameraId] === 'connected'
                      ? {
                          'homeVideo_main-event': secondaryFrameState(
                            cameraId,
                            secondaryIndex + 1
                          ).event,
                          homeVideo_hide: secondaryIndex === draggingOverIndex,
                          'homeVideo_selected-capture': secondaryFrameState(
                            cameraId,
                            secondaryIndex + 1
                          ).capture,
                          'homeVideo_selected-trim': secondaryFrameState(
                            cameraId,
                            secondaryIndex + 1
                          ).trim,
                          homeVideo_unselected: secondaryFrameState(
                            cameraId,
                            secondaryIndex + 1
                          ).unselected,
                        }
                      : {}
                  "
                  :draggable="isCurrentAvatarOwner"
                  :show-right-controller="true"
                  :bottom-controller="{
                    mode: captureMode || trimMode ? 'select' : 'controller',
                    selectMode: captureMode
                      ? 'capture'
                      : trimMode
                      ? 'trim'
                      : 'none',
                    selected: camSelectItems[secondaryIndex + 1]
                      ? camSelectItems[secondaryIndex + 1].selected
                      : false,
                  }"
                  @timeupdate="handleVideoStreamTimeUpdate($event, cameraId)"
                  @onFullscreenChange="handleFullscreenChange"
                  @setTime="getTime"
                  @onSelect="handleSelectCamera($event, secondaryIndex + 1)"
                  @onCameraStatus="handleCamStatus"
                  @onLiveMode="handleLiveMode"
                  @onMouseover="handleVideoMouseover"
                  @onMouseLeave="handleVideoMouseleave"
                  @onAction="handleVideoStreamAction"
                  @onWaiting="handleWaitingVideoData"
                  @onSeeking="handleSeeking"
                  @onLoading="handleCameraLoading"
                >
                  <template slot-scope="{ show, _class }">
                    <VideoSeek
                      name="HomeVideo-secondary"
                      :v-show="show"
                      :class="_class"
                      fullscreen-mode
                      :live.sync="live"
                      :playing.sync="playing"
                      :playback-rate.sync="playbackRate"
                      :show-playback-rate="isAnyCamConnected"
                      :timestamp="
                        currentLiveMode === 'WebRTC' && live
                          ? dateTimeNow
                          : timestamp
                      "
                      :max-days="maxRetentionPeriodDays"
                      :can-edit-date-time="canEditVideoSeekDateTime"
                      :time-range="currentTimeRange"
                      @onLive="handleLive"
                      @changeCurrentTime="handleChangeCurrentTime"
                      @onGetDate="handleGetDate"
                      @onSetTimeRange="handleSetTimeRange"
                    />
                  </template>
                </video-stream>
              </div>
            </template>
          </draggable>
        </div>
      </section>

      <timeslapse-dialog
        :record-time="trimDuration"
        :open="timeslapseOpen"
        @onClose="handleCloseTimeslapse"
        @onTrim="handleTrim"
      />
      <div
        v-if="!loading"
        class="homeVideo_control-box-gap"
        :style="{ height: `${controlBoxGap}px` }"
        :class="{
          ['homeVideo_control-box--tools']: captureMode || trimMode,
        }"
      />
      <div
        ref="seekWrapper"
        class="homeVideo_control-box"
        :class="{
          ['homeVideo_control-box--active']: !loading,
          ['homeVideo_control-box--tools']: captureMode || trimMode,
        }"
      >
        <VideoSeek
          ref="mainSeek"
          name="HomeVideo"
          :capture-mode="captureMode"
          :trim-mode="trimMode"
          :events="videoSeekEventList"
          :live.sync="live"
          :playing.sync="playing"
          :playback-rate.sync="playbackRate"
          :timestamp="
            currentLiveMode === 'WebRTC' && live ? dateTimeNow : timestamp
          "
          :max-days="maxRetentionPeriodDays"
          :show-capture="showVideoSeekCapture"
          :show-trim="showVideoSeekTrim"
          :can-edit-date-time="canEditVideoSeekDateTime"
          :timeline-width="timelineWidth"
          :show-playback-rate="isAnyCamConnected"
          :time-range="currentTimeRange"
          @toggleCaptureMode="toggleCaptureMode"
          @toggleTrimMode="toggleTrimMode"
          @setTime="getTime"
          @onTrimChange="handleTrimChange"
          @onLive="handleLive"
          @changeCurrentTime="handleChangeCurrentTime"
          @onGetDate="handleGetDate"
          @onSetTimeRange="handleSetTimeRange"
        />
        <div class="homeVideo_tools">
          <div v-if="captureMode || trimMode" class="homeVideo_edit">
            <div class="homeVideo_edit-right">
              <v-btn
                color="text"
                class="homeVideo_edit-cancel"
                @click="cancelEdit()"
              >
                キャンセル
              </v-btn>
              <v-btn
                class="homeVideo_edit-shootbtn"
                color="primary"
                :disabled="!isCamSelected || (!captureMode && trimDisabled)"
                @click="toggleShoot()"
              >
                <span v-if="captureMode" class="icon icon-camera2"></span>
                <span v-else class="icon icon-cut"></span>
              </v-btn>
              <v-btn
                v-if="!captureMode && false"
                color="text"
                class="homeVideo_edit-timelapse"
                :disabled="!isCamSelected || trimDisabled"
                @click="handleOpenTimeslapse"
              >
                タイムラプス
              </v-btn>
              <div v-else class="homeVideo_edit-btn_gap"></div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </v-sheet>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions, mapGetters, mapState } from 'vuex';
import VideoService from '@/services/video.service';
import VideoSeek from '@/components/VideoSeek.vue';
import TrimTransition from '@/components/animations/TrimTransition.vue';
import TestKvWebRtc from '@/components/TestKVWebRTC.vue';
import draggable from 'vuedraggable';
import HomeSkeleton from '@/components/skeletons/HomeSkeleton.vue';
import VideoSeekMixin from '../../mixin/videoSeekMixin.js';
import EventBus from '@/utils/eventBus';
import {
  CallerKeys,
  CameraStatus,
  EventBusEvents,
  HistoryCode,
  KVSErrorCodes,
  PlayerStatus,
  VideoControllersType,
} from '@/globals/enums.js';
import {
  convertHoursToDays,
  getEvenLabelStyle,
  getMaxValueFromObjects,
  getVideoClipName,
} from '@/utils/helpers.js';
import TimeslapseDialog from '@/components/dialogs/TimeslapseDialog.vue';
import { videoService } from '../../services/video.service.js';
import { DEFAULT_TIME_RANGE, TIME_RANGE_DURATION } from '@/globals/constants';
import isEqual from 'fast-deep-equal';
import { logEvent } from '@/services/log.service';
import { getEventStartWithOffset } from '@/utils/event';

const customParseFormat = require('dayjs/plugin/customParseFormat');
const duration = require('dayjs/plugin/duration');

dayjs.extend(customParseFormat);
dayjs.extend(duration);

export default {
  name: 'HomeVideo',
  components: {
    VideoSeek,
    draggable,
    TrimTransition,
    HomeSkeleton,
    TestKvWebRtc,
    TimeslapseDialog,
  },
  mixins: [VideoSeekMixin],
  data() {
    return {
      videoTimeBox: {},
      videoService: new VideoService(),
      startTimestamp: dayjs().subtract(1, 's').valueOf(),
      endTimestamp: dayjs().valueOf(),
      currentLiveMode: 'HLS',
      dateTimeNow: dayjs().valueOf(),
      timestampAfterPageUpdate: null,
      syncVODInterval: null,
      waitingVideoData: {},
      actionOnMount: {},
      mountState: {},
      isAnyCamConnected: false,
      isAnyCamInGap: false,
      showVideoStream: true,
      cameraLoading: false,
      currentTimeIsChanging: false,
      handlingCurrentHomeGroupChange: false,
      currentTimeRange: DEFAULT_TIME_RANGE,
      canAutoplay: true,
      page: this.$store.state.common.initialPage || 1,
      eventId: null,
      events: [],
      fullscreen: false,
      mainCams: [],
      secondaryCams: [],
      movingIndex: 0,
      futureIndex: 0,
      firstConnectedCameraId: null,
      movedTo: '',
      movedFrom: '',
      captureMode: false,
      trimMode: false,
      trimStart: 0,
      trimEnd: 0,
      trimMovePoint: null,
      trimMinRange: false,
      durationFactor: 1,
      transitionImages: {},
      cameraStatus: {},
      camSelectItems: {
        0: {
          selected: false,
        },
        1: {
          selected: false,
        },
        2: {
          selected: false,
        },
      },
      resizeObserver: null,
      controlBoxGap: 10,
      secondariesHeight: 0,
      timeslapseOpen: false,
      draggingOverIndex: null,
      mainDragIndex: 3,
      homeVideosMaxWidth: {},
      timelineWidth: 0,
      attachingCredentials: false,
      credentials: {},
      prevActiveEvents: [],
      loggedLiveCameras: new Set(),
      loggedRecordedCameras: new Set(),
      previousLiveState: true,
      displayEventInProgressKey: null,
      pendingEventLogging: null,
      lastDisplayedEventId: null,
      eventDisplayCallSource: null,
    };
  },
  computed: {
    ...mapGetters('common', {
      currentGroupCamList: 'currentGroupCamList',
      maxRetentionPeriodHours: 'maxRetentionPeriodHours',
      maxRetentionPeriodDays: 'maxRetentionPeriodDays',
    }),
    ...mapState({
      testMode: (state) => state.common.testMode,
      appReady: (state) => state.common.appReady,
      iamKey: (state) => state.common.iam.accessKeyId,
      iamSecret: (state) => state.common.iam.secretAccessKey,
      cameraById: (state) => state.common.cameraById,
      homeCameraGroups: (state) => state.common.homeCameraGroups,
      currentCameraPolicy: (state) => state.common.currentCameraPolicy,
      activeEvents: (state) => state.home.activeEvents,
      enterPageLoading: (state) => state.home.enterPageLoading,
      currentHomeGroup: (state) => state.common.currentHomeGroup,
      selectedEvent: (state) => state.event.event,
      videoSeekEventList: (state) => state.event.seekbarEvents,
      currentCameraDirectoryId: (state) =>
        state.settings.currentCameraDirectoryId,
      isCamSelected() {
        return Object.keys(this.camSelectItems).some(
          (key) => this.camSelectItems[key].selected
        );
      },
      isCurrentAvatarOwner: (state) => state.settings.isCurrentAvatarOwner,
      timeRange: (state) => state.home.timeRange,
    }),
    showVideoSeekCapture() {
      return this.isCurrentAvatarOwner || !this.currentCameraPolicy.liveOnly;
    },
    showVideoSeekTrim() {
      return this.isCurrentAvatarOwner || !this.currentCameraPolicy.liveOnly;
    },
    canEditVideoSeekDateTime() {
      return this.isCurrentAvatarOwner || !this.currentCameraPolicy.liveOnly;
    },
    showCameraAbout() {
      return (
        (!this.currentCameraPolicy.access &&
          !this.currentCameraPolicy.liveOnly) ||
        this.isCurrentAvatarOwner
      );
    },
    showAutoFocus() {
      return !this.currentCameraPolicy.liveOnly || this.isCurrentAvatarOwner;
    },
    showEvent() {
      let show =
        Object.entries(this.cameraActiveEvents).some((ca) => !!ca) &&
        !this.live;
      return show;
    },
    loading() {
      return (
        this.cameraLoading ||
        !this.appReady ||
        this.attachingCredentials ||
        this.enterPageLoading
      );
    },
    trimDisabled() {
      return dayjs(this.trimEnd).isAfter(dayjs());
    },
    trimDuration() {
      const startDate = dayjs(this.trimStart);
      const endDate = dayjs(this.trimEnd);
      const duration = dayjs.duration(endDate.diff(startDate)).asMilliseconds();
      return duration;
    },
    pageCount() {
      return this.currentGroupCamList.length || 0;
    },
    cameras() {
      let cams = {};
      this.currentGroupCamList.forEach((cam, index) => {
        cams = { ...cams, [index]: cam };
      });
      return cams;
    },
    mainCam() {
      return {
        ...this.cameraById[this.mainCams[0]],
        ...this.credentials[this.mainCams[0]],
      };
    },
    secondariesCam() {
      // map object by cam id
      return [
        {
          ...this.cameraById[this.secondaryCams[0]],
          ...this.credentials[this.secondaryCams[0]],
        },
        {
          ...this.cameraById[this.secondaryCams[1]],
          ...this.credentials[this.secondaryCams[1]],
        },
      ];
    },
    allHomeCamsId() {
      return [this.mainCams[0], this.secondaryCams[0], this.secondaryCams[1]];
    },
    allHomeCams() {
      return [
        this.cameraById[this.mainCams[0]],
        this.cameraById[this.secondaryCams[0]],
        this.cameraById[this.secondaryCams[1]],
      ];
    },
    isSafari() {
      return this.$device.isSafari;
    },
    cameraActiveEvents() {
      const cameraEvents = {
        [this.mainCams[0]]: this.activeEvents.find(
          (event) => +event.cameraId === +this.mainCams[0]
        ),
        [this.secondaryCams[0]]: this.activeEvents.find(
          (event) => +event.cameraId === +this.secondaryCams[0]
        ),
        [this.secondaryCams[1]]: this.activeEvents.find(
          (event) => +event.cameraId === +this.secondaryCams[1]
        ),
      };

      return cameraEvents;
    },
  },
  watch: {
    selectedEvent: {
      handler: function (val) {
        // check if this is a duplicate call
        if (
          val &&
          this.lastDisplayedEventId &&
          String(val.eventId) === String(this.lastDisplayedEventId)
        ) {
          console.warn('Duplicate event detected: Event already displayed', {
            eventId: val.eventId,
            lastDisplayedEventId: this.lastDisplayedEventId,
          });
          return;
        }

        if (!val) {
          return;
        }

        this.eventDisplayCallSource = 'selectedEvent-watcher';
        this.$nextTick(() => {
          this.displayEvent(val);
        });
      },
    },
    /**
     * watches changes to the current group camera list and updates the displayed cameras accordingly.
     * ensures that the main and secondary cameras are rearranged based on the current page index.
     */
    currentGroupCamList: {
      handler: function (val) {
        const startIndex = (this.page - 1) % val.length;
        const indexes = Array.from(
          { length: Math.min(3, val.length) },
          (_, i) => (startIndex + i) % val.length
        );

        indexes.forEach((index, displayIndex) => {
          if (val[index]) {
            this.rearrangeHomeCam({ ...val[index] }, displayIndex);
          }
        });
      },
      immediate: true,
    },
    $route: {
      handler: function (to, from) {
        this.handleRouteChange(to, from);
      },
      immediate: true,
      deep: true,
    },
    currentHomeGroup: {
      handler(val) {
        this.handleCurrentHomeGroupChange(val);
      },
      immediate: true,
    },
    maxRetentionPeriodDays(newVal) {
      this.setMaxRetentionPeriodDays(newVal);
    },
  },
  mounted() {
    this.currentTimeRange = DEFAULT_TIME_RANGE;

    this.setHomeSeekStart(null);
    if (this.$refs.seekWrapper) {
      this.resizeObserver = new ResizeObserver(
        this.handleVideoSeekResize
      ).observe(this.$refs.seekWrapper);
      this.timelineWidth = this.$refs.seekWrapper.clientWidth;
    } else {
      console.log('[WARN] seekWrapper ref not found');
    }

    this.syncVOD();
    EventBus.on(EventBusEvents.TrimEvent, this.handleTrimEvent);
    EventBus.on(EventBusEvents.CamGroupChanged, this.handleHomeGroupChanged);
    EventBus.on(EventBusEvents.DisplayEvent, this.handleDisplayEventFromBus);
    EventBus.on(
      EventBusEvents.AuthFailureCleanup,
      this.handleAuthFailureCleanup
    );
    window.addEventListener('resize', this.handleWindowResize);

    // check for eventid before clearing initialPage
    const hasEventId = !!this.$route.query.eventid;
    const selectedEvent = this.$store.state.event.event;
    const initialPage = this.$store.state.common.initialPage;

    // clear initial page after component is mounted
    // this ensures it is only used for initial setup, not subsequent navigations
    if (initialPage !== null) {
      this.$store.dispatch('common/clearInitialPage');
    }

    // if event was preprocessed, display it IMMEDIATELY
    // this ensures event timestamps are set before VideoStream components mount
    if (selectedEvent && hasEventId) {
      // track call source
      this.eventDisplayCallSource = 'mounted-hook';

      // display event immediately - do not wait for nextTick
      // this ensures timestamps are set before VideoStream components mount
      this.displayEvent(selectedEvent, true);
    }
  },
  beforeDestroy() {
    clearTimeout(window.timeoutdisplayLive);
    clearTimeout(window.waitForCamTimeout);
    clearTimeout(window.timeoutdisplayEvent);
    clearInterval(this.syncVODInterval);
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs.seekWrapper);
    }
    EventBus.off(EventBusEvents.TrimEvent, this.handleTrimEvent);
    EventBus.off(EventBusEvents.CamGroupChanged, this.handleHomeGroupChanged);
    EventBus.off(EventBusEvents.DisplayEvent, this.handleDisplayEventFromBus);
    EventBus.off(
      EventBusEvents.AuthFailureCleanup,
      this.handleAuthFailureCleanup
    );

    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    async init() {},
    handleDisplayEventFromBus(event) {
      this.lastDisplayedEventId = null;
      this.$nextTick(() => {
        this.displayEvent(event);
      });
    },

    /**
     * Handle auth failure cleanup event
     * Stop credential renewal attempts and clear local state
     */
    handleAuthFailureCleanup() {
      // stop attaching credentials if in progress
      this.attachingCredentials = false;

      // clear all credentials
      this.credentials = {};

      // clear all camera statuses and reset state
      this.cameraStatus = {};
      this.waitingVideoData = {};
      this.clearAllLoggedCameras();
    },

    getDisplayedCameraIds() {
      const cameraIds = [];
      if (this.mainCams[0]) {
        cameraIds.push(this.mainCams[0]);
      }
      if (this.secondaryCams[0]) {
        cameraIds.push(this.secondaryCams[0]);
      }
      if (this.secondaryCams[1]) {
        cameraIds.push(this.secondaryCams[1]);
      }
      return cameraIds.filter(Boolean);
    },
    clearAllLoggedCameras() {
      this.loggedLiveCameras.clear();
      this.loggedRecordedCameras.clear();
    },
    async logLivePlaybackForCameras(cameraIds) {
      if (!cameraIds || cameraIds.length === 0) {
        return;
      }

      const cameraIdsToLog = cameraIds.filter(
        (id) => !this.loggedLiveCameras.has(String(id))
      );

      if (cameraIdsToLog.length === 0) {
        return;
      }

      for (const cameraId of cameraIdsToLog) {
        try {
          await logEvent(HistoryCode.LiveVideoPlayback, cameraId);
          this.loggedLiveCameras.add(String(cameraId));
        } catch (error) {
          console.error(
            `Failed to log live playback for camera ${cameraId}:`,
            error
          );
        }
      }
    },
    async logRecordedPlaybackForCameras(cameraIds) {
      if (!cameraIds || cameraIds.length === 0) {
        return;
      }

      const cameraIdsToLog = cameraIds.filter(
        (id) => !this.loggedRecordedCameras.has(String(id))
      );

      if (cameraIdsToLog.length === 0) {
        return;
      }

      for (const cameraId of cameraIdsToLog) {
        try {
          await logEvent(HistoryCode.RecordedVideoPlayback, cameraId);
          this.loggedRecordedCameras.add(String(cameraId));
        } catch (error) {
          console.error(
            `Failed to log recorded playback for camera ${cameraId}:`,
            error
          );
        }
      }
    },
    async logPlaybackForDisplayedCameras(isLive) {
      const displayedCameraIds = this.getDisplayedCameraIds();
      if (isLive) {
        await this.logLivePlaybackForCameras(displayedCameraIds);
      } else {
        await this.logRecordedPlaybackForCameras(displayedCameraIds);
      }
    },

    async switchPlaybackModeAndLog(isLive) {
      this.clearAllLoggedCameras();
      await this.logPlaybackForDisplayedCameras(isLive);
    },
    getCameraTitle(cameraId) {
      const camera = this.cameraById[cameraId];
      if (!camera) {
        console.warn(`Camera with ID ${cameraId} not found`);
        return '';
      }
      return camera.name;
    },

    getMinimumCalendarDate(cameraGroupId) {
      if (!this.homeCameraGroups[cameraGroupId]) {
        if (cameraGroupId === 0) {
          return this.calculateMinimumDate(this.cameraList);
        }

        console.error(`Invalid cameraGroupId: ${cameraGroupId}`);
        return dayjs(); // Default to the current date
      }

      return this.calculateMinimumDate(
        this.homeCameraGroups[cameraGroupId].cameraList
      );
    },
    calculateMinimumDate(cameraList) {
      const maxRetentionPeriod = getMaxValueFromObjects(
        cameraList,
        'retentionPeriod'
      );
      const maxRetentionDays = convertHoursToDays(maxRetentionPeriod);
      return dayjs().subtract(maxRetentionDays, 'days');
    },
    checkCalendarInRetentionPeriod(cameraGroupId) {
      // check if the current timestamp is before the minimum selectable date
      // if so, set the timestamp to the minimum selectable date
      // and set the startTimestamp to the minimum selectable date as well
      const minimumSelectableDate = this.getMinimumCalendarDate(cameraGroupId);
      if (!dayjs(this.timestamp).isBefore(minimumSelectableDate)) {
        return null; // no changes needed
      }
      this.timestamp = minimumSelectableDate.valueOf();
      this.startTimestamp = minimumSelectableDate.valueOf();
      this.timestampAfterPageUpdate = null;

      return {
        endtime: Math.min(
          dayjs(this.timestamp)
            .add(this.currentTimeRange * TIME_RANGE_DURATION, 'minutes')
            .valueOf(),
          dayjs().valueOf()
        ),
      };
    },
    handleWaitingVideoData({ id, status }) {
      if (+this.firstConnectedCameraId === +id) {
        // reset synchronization interval
        this.syncVOD();
      }
      this.$set(this.waitingVideoData, id, status);
    },
    handleSeeking({ id }) {
      if (+this.firstConnectedCameraId === +id) {
        // reset synchronization interval
        this.syncVOD();
      }
    },
    handleCameraLoading({ id }) {
      if (+this.firstConnectedCameraId === +id) {
        // reset synchronization interval
        this.syncVOD();
      }
    },
    async updateCredentials(ids) {
      if (ids.length === 0) {
        console.warn(
          '[HomeVideo] No camera IDs provided for updating credentials'
        );
        return;
      }
      try {
        this.attachingCredentials = true;

        const credentials = await videoService.attachCameraIamAccessKey(
          ids.map((id) => ({ id }))
        );

        // set main camera credentials
        if (this.mainCams[0] && credentials[this.mainCams[0]]) {
          this.$set(this.credentials, this.mainCams[0], {
            accessKeyId: credentials[this.mainCams[0]].accessKeyId,
            secretAccessKey: credentials[this.mainCams[0]].secretAccessKey,
            sessionToken: credentials[this.mainCams[0]].sessionToken,
          });
        }

        // set secondary cameras credentials
        this.secondaryCams.forEach((id) => {
          if (id && credentials[id]) {
            this.$set(this.credentials, id, {
              accessKeyId: credentials[id].accessKeyId,
              secretAccessKey: credentials[id].secretAccessKey,
              sessionToken: credentials[id].sessionToken,
            });
          }
        });
      } catch (error) {
        console.error('Error attaching credentials', error);
      } finally {
        this.attachingCredentials = false;
      }
    },
    async handleKVSExpiration({ timestamp }) {
      if (this.attachingCredentials) {
        return;
      }
      console.warn(
        `[WARN][${dayjs().format(
          'HH:mm:ss'
        )}] KVS security token is expired. We are going to renew token. Current timestamp is ${dayjs(
          this.timestamp
        ).format('HH:mm:ss')}`
      );
      this.startTimestamp = timestamp + 0;
      this.updateCredentials([...this.mainCams, ...this.secondaryCams]);
    },
    async handleStreamingSessionExpiration({ timestamp }) {
      if (this.attachingCredentials) {
        return;
      }
      console.warn(
        `[WARN][${dayjs().format(
          'HH:mm:ss'
        )}] Streaming session is expired. We are going to renew token. Current timestamp is ${dayjs(
          this.timestamp
        ).format('HH:mm:ss')}`
      );
      this.startTimestamp = timestamp + 0;
      this.updateCredentials([...this.mainCams, ...this.secondaryCams]);
    },
    handleVideoStreamAction(e) {
      if (e.type === 'error') {
        const { error, code, timestamp } = e.payload;

        if (error) {
          switch (code) {
            case KVSErrorCodes.GET_DATA_ENDPOINT_TOKEN_EXP:
              this.handleKVSExpiration({ error, code, timestamp });
              break;
            case PlayerStatus.StreamingSessionExpired:
              this.handleStreamingSessionExpiration({ error, code, timestamp });
              break;

            default:
              break;
          }
        }
      }
      // currently this function is only used for debugging purpose
      else if (e.type === 'mounted') {
        this.$set(this.mountState, e.payload.id, true);
      } else if (e.type === 'beforeDestroy') {
        this.$set(this.mountState, e.payload.id, false);
      } else if (e.type === 'switchLive') {
        this.handleLive();
      }
      if (this.actionOnMount[e.payload.id]) {
        const action = this.actionOnMount[e.payload.id];
        action();
        this.$set(this.actionOnMount, e.payload.id, undefined);
      }
    },
    handleCurrentHomeGroupChange() {
      this.handlingCurrentHomeGroupChange = true;
      this.clearAllLoggedCameras();

      // check if initialPage is set (from eventid preprocessing)
      const initialPage = this.$store.state.common.initialPage;
      const targetPage = initialPage !== null ? initialPage : 1;
      const hasEventId = !!this.$route.query.eventid;
      const selectedEvent = this.$store.state.event.event;
      const shouldSkipLive = hasEventId && selectedEvent;

      if (this.$refs.paginate) {
        this.page = targetPage;
        if (targetPage === 1) {
          this.$refs.paginate.selectFirstPage();
        } else {
          this.$refs.paginate.handlePageSelected(targetPage);
        }
      }
      this.cancelEdit();
      this.clearHomeCams();
      const layouts = [];
      this.$nextTick(async () => {
        const arrangeList = [];

        // ensure we have cameras to display
        if (
          !this.currentGroupCamList ||
          this.currentGroupCamList.length === 0
        ) {
          console.warn('No cameras available, aborting group change');
          this.handlingCurrentHomeGroupChange = false;
          return;
        }

        // calculate which cameras to display based on targetPage
        const startIndex = (targetPage - 1) % this.currentGroupCamList.length;
        const indexes = Array.from(
          { length: Math.min(3, this.currentGroupCamList.length) },
          (_, i) => (startIndex + i) % this.currentGroupCamList.length
        );

        indexes.forEach((camIndex, displayIndex) => {
          const cam = this.currentGroupCamList[camIndex];
          if (cam) {
            layouts.push({
              index: displayIndex,
              id: cam.id,
              liveMode: this.getCameraLiveMode(cam.id),
            });
            arrangeList.push({ cam: { ...cam }, index: displayIndex });
          }
        });

        // only set LIVE timestamps if we are not loading an event
        // if eventid exists, displayEvent() will set the correct timestamps
        if (shouldSkipLive) {
          // do not set timestamps here - displayEvent() will handle it
          // keep existing timestamps or use event timestamps if available
          if (selectedEvent && selectedEvent.startEventUnix) {
            this.startTimestamp = selectedEvent.startEventUnix;
            let endTimestamp = dayjs(selectedEvent.startEventUnix)
              .add(22, 'hour')
              .valueOf();
            if (endTimestamp > dayjs().valueOf()) {
              endTimestamp = dayjs().subtract(1, 's').valueOf();
            }
            this.endTimestamp = endTimestamp;
          }
        } else {
          this.startTimestamp = this.live
            ? dayjs().subtract(1, 's').valueOf()
            : this.timestamp || this.startTimestamp;

          if (!this.canEditVideoSeekDateTime) {
            this.setupLive();
          }
        }

        arrangeList.forEach((item) => {
          this.rearrangeHomeCam(item.cam, item.index);
        });

        await this.updateCredentials(layouts.map((item) => item.id));

        this.$nextTick(() => {
          this.changeCurrentLiveMode();
          this.logPlaybackForDisplayedCameras(this.live);
          this.handlingCurrentHomeGroupChange = false;
        });
      });
    },
    async handleHomeGroupChanged({ group }) {
      const shouldReset = this.live || !this.canEditVideoSeekDateTime;
      let endtime = undefined;
      if (!shouldReset) {
        const checkResult = this.checkCalendarInRetentionPeriod(group);

        if (checkResult) {
          endtime = checkResult.endtime;
        } else {
          console.warn('Failed to check calendar retention period.');
        }
      }

      this.refetchHomeEvents({
        timestamp: this.timestamp,
        endtime: endtime,
        timeRange: this.currentTimeRange,
        shouldReset: shouldReset,
        caller: CallerKeys.HomeVideo_handleHomeGroupChanged,
      });
    },
    handleVideoMouseover() {
      this.changeSecondariesHeight();
    },
    handleVideoMouseleave() {
      const secondariesElm = document.getElementsByClassName(
        'homeVideo_secondaries'
      )[0];
      secondariesElm.style['max-height'] = 'initial';
    },
    handleDragStart() {},
    clearHomeCams() {
      this.mainCams = [];
      this.secondaryCams = [];
    },
    rearrangeHomeCam(cam, index) {
      if (index === 0) {
        this.mainCams = [cam.id];
        this.$set(this.camSelectItems[0], 'id', cam.id);
      } else if (index === 1) {
        this.$set(this.secondaryCams, 0, cam.id);
        this.$set(this.camSelectItems[1], 'id', cam.id);
      } else if (index === 2) {
        this.$set(this.secondaryCams, 1, cam.id);
        this.$set(this.camSelectItems[2], 'id', cam.id);
      }
    },
    updateStartTimestampWithSeekbar() {
      // When remount VideStream, we should change `startTimestamp` value with current video seek `timestamp` to continue the current timestamp.
      // Note: VideStream component will start the video from `startTimestamp` on mounting.
      // If `timestampAfterPageUpdate` is set, the update for `startTimestamp` will be set based on `timestampAfterPageUpdate`.
      // In the case of `event` after selecting an event, the camera page may change and `updateStartTimestampWithSeekbar` will be called.
      // To start the video stream from the time of selected `event` we should set `startTimestamp` based on event start time.
      if (this.timestampAfterPageUpdate) {
        this.startTimestamp = this.timestampAfterPageUpdate + 0;
        this.timestampAfterPageUpdate = null;
      } else {
        this.startTimestamp = this.timestamp;
      }
    },
    getCameraLayoutByActivePages(activePages) {
      const layouts = [];
      const arrangeList = [];

      activePages.forEach(({ index: camIndex }, index) => {
        const cam = this.currentGroupCamList[camIndex];
        // ensure cam is not undefined before proceeding
        if (cam && index < 3) {
          layouts.push({
            index,
            id: cam.id,
            liveMode: this.getCameraLiveMode(cam.id),
            retentionPeriod: cam.retentionPeriod,
          });
        }
        if (cam) {
          arrangeList.push({ cam: { ...cam }, index });
        }
      });

      return { layouts, arrangeList };
    },

    async handlePage({ activePages }) {
      if (this.handlingCurrentHomeGroupChange) {
        return;
      }
      this.videoTimeBox = {};

      this.cancelEdit();
      this.clearHomeCams();
      const { layouts, arrangeList } =
        this.getCameraLayoutByActivePages(activePages);

      this.updateStartTimestampWithSeekbar();

      arrangeList.forEach((item) => {
        this.rearrangeHomeCam(item.cam, item.index);
      });
      // attach credentials
      await this.updateCredentials(layouts.map((item) => item.id));

      const pendingLogging = this.pendingEventLogging;
      this.pendingEventLogging = null;

      this.$nextTick(() => {
        this.changeCurrentLiveMode();
        if (pendingLogging === false) {
          // do not clear sets (preserve existing logged cameras)
          // but still log new cameras that are not already logged
          this.logPlaybackForDisplayedCameras(this.live);
        } else if (pendingLogging) {
          this.clearAllLoggedCameras();
          this.logPlaybackForDisplayedCameras(pendingLogging.isLive);
        } else {
          this.logPlaybackForDisplayedCameras(this.live);
        }
      });
    },
    handleOpenTimeslapse() {
      this.timeslapseOpen = true;
    },
    handleCloseTimeslapse() {
      this.timeslapseOpen = false;
    },
    changeCurrentLiveMode() {
      let cameraForLiveMode = this.cameraById[this.firstConnectedCameraId];
      if (!cameraForLiveMode) {
        cameraForLiveMode = this.cameras[0];
      }

      if (cameraForLiveMode) {
        this.currentLiveMode = this.getCameraLiveMode(cameraForLiveMode.id);
      } else {
        console.warn(
          '[WARN] Trying to change current live mode but cameras not loaded!'
        );
      }
    },
    getEventLabelStyle(label) {
      const style = getEvenLabelStyle(label);
      const labelStyle = {
        color: style?.color,
        'background-color': style?.bgColor,
      };

      return labelStyle;
    },
    mainFrameState() {
      if (!this.cameraById[this.mainCams[0]]) {
        return {};
      }
      const state = {};
      if (this.showEvent && !!this.cameraActiveEvents[this.mainCams[0]]) {
        state.event = true;
      }
      if (this.movedTo === 'main') {
        state.drag = true;
      }

      if (
        this.camSelectItems[0] &&
        this.camSelectItems[0].selected &&
        this.captureMode &&
        !this.fullscreen
      ) {
        state.capture = true;
      }

      if (
        this.camSelectItems[0] &&
        this.camSelectItems[0].selected &&
        this.trimMode &&
        !this.fullscreen
      ) {
        state.trim = true;
      }

      if (
        (this.trimMode || this.captureMode) &&
        this.camSelectItems[0] &&
        !this.camSelectItems[0].selected &&
        !this.fullscreen
      ) {
        state.unselected = true;
      }
      return state;
    },
    secondaryFrameState(cameraId, currentIndex) {
      const state = {};
      if (this.showEvent && !!this.cameraActiveEvents[cameraId]) {
        state.event = true;
      }
      if (this.movedTo === 'secondary' && this.futureIndex === currentIndex) {
        state.drag = true;
      }

      if (
        this.camSelectItems[currentIndex] &&
        this.camSelectItems[currentIndex].selected &&
        this.captureMode &&
        !this.fullscreen
      ) {
        state.capture = true;
      }
      if (
        this.camSelectItems[currentIndex] &&
        this.camSelectItems[currentIndex].selected &&
        this.trimMode &&
        !this.fullscreen
      ) {
        state.trim = true;
      }

      if (
        (this.trimMode || this.captureMode) &&
        this.camSelectItems[currentIndex] &&
        !this.camSelectItems[currentIndex].selected &&
        !this.fullscreen
      ) {
        state.unselected = true;
      }
      return state;
    },
    changeSecondariesHeight() {
      const secondariesElm = document.getElementsByClassName(
        'homeVideo_secondaries'
      )[0];
      this.secondariesHeight =
        this.$refs.secondaries.$el.getBoundingClientRect().height;
      secondariesElm.style['max-height'] = this.secondariesHeight + 'px';
    },
    handleVideoSeekResize() {
      if (this.$refs.seekWrapper) {
        this.controlBoxGap =
          this.$refs.seekWrapper.getBoundingClientRect().height;
      }
    },
    handleLiveMode({ mode }, isMainCam) {
      if (isMainCam) {
        this.currentLiveMode = mode;
      }
    },
    waitForCam({ type, status, camId, timeout }) {
      return new Promise((resolve, reject) => {
        const listenPlaySuccess = (action) => {
          if (window.waitForCamTimeout) {
            clearTimeout(window.waitForCamTimeout);
          }
          window.waitForCamTimeout = setTimeout(() => {
            reject(
              new Error(
                `Timeout waiting for cam ${camId} with action type ${type} to ${status}`
              )
            );
          }, timeout || 15000);
          if (
            action.type === (type || 'playerStatus') &&
            action.payload.status === (status || CameraStatus.Connected) &&
            action.meta &&
            action.meta.id === camId
          ) {
            EventBus.off(EventBusEvents.CallVideoStream, listenPlaySuccess);
            resolve();
          }
        };
        EventBus.on(EventBusEvents.CallVideoStream, listenPlaySuccess);
      });
    },
    changeCameraLoading(value) {
      this.cameraLoading = value;
    },
    async handleRouteChange(to, from) {
      // the page is reloaded
      if (!from) {
        this.init();
      } else if (to.query.live) {
        this.logPlaybackForDisplayedCameras(true);
        this.displayLive();
      }
      if (to.name === 'home' && this.live) {
        this.$nextTick(() => {
          this.logPlaybackForDisplayedCameras(true);
        });
      } else if (to.name === 'home' && !this.live) {
        this.$nextTick(() => {
          this.logPlaybackForDisplayedCameras(false);
        });
      } else if (to.name !== 'home') {
        this.clearAllLoggedCameras();
      }
    },
    recreateVideoStream() {
      return new Promise((resolve) => {
        this.showVideoStream = false;
        setTimeout(() => {
          this.showVideoStream = true;
          resolve();
        }, 500);
      });
    },
    /**
     * Check if all videos are synced with the main camera with specific tolerance.
     * If the main camera is not connected then check two other cameras to be synced.
     * If not sync return the cameras need to be synced and warn.
     */
    checkAllVideosTimeSync() {
      const cameraAvailabiliy = [
        CameraStatus.Connected,
        PlayerStatus.Discontinuity,
        KVSErrorCodes.NO_FRAGMENT_FOUND,
        PlayerStatus.OutOfRetentionPeriod,
      ];
      const tolerance = 15000; // 15 seconds tolerance
      const syncedCams = [];
      const notSyncedCams = [];
      const connectedCams = [...this.mainCams, ...this.secondaryCams].filter(
        (camId) => cameraAvailabiliy.includes(this.cameraStatus[camId])
      );

      if (connectedCams.length < 2) {
        return { syncedCams, notSyncedCams };
      }

      const mainCamId = connectedCams[0];
      const mainCamTimestamp = this.videoTimeBox[mainCamId];
      for (const camId in this.videoTimeBox) {
        if (!connectedCams.includes(+camId)) continue;
        if (+camId === +mainCamId) continue;

        if (
          Math.abs(
            dayjs(this.videoTimeBox[camId]).diff(dayjs(mainCamTimestamp))
          ) <= tolerance
        ) {
          syncedCams.push(camId);
        } else {
          notSyncedCams.push(camId);
        }
      }
      if (notSyncedCams.length > 0) {
        console.warn(
          `[WARN] ${
            notSyncedCams.length
          } cameras are not synced with the main camera.
          The tolerance is ${tolerance}ms.
          The main camera time is ${dayjs(mainCamTimestamp).format('HH:mm:ss')}.
          With camera name and timestamp:
          ${notSyncedCams
            .map(
              (camId) =>
                `${this.cameraById[camId].name} - ${dayjs(
                  this.videoTimeBox[camId]
                ).format('HH:mm:ss')}`
            )
            .join('\n')}
          `
        );
      }
      return { syncedCams, notSyncedCams, syncTimestamp: mainCamTimestamp };
    },
    /**
     * Call `checkAllVideosTimeSync` in an interval to make sure all videos are synced with the main camera.
     * if not sync, call `handleChangeCurrentTime` to sync the video with the main camera.
     */
    syncVOD() {
      // if live, or not playing, or current time is changing, don't sync video

      clearInterval(this.syncVODInterval);
      const interval = 5000;
      this.syncVODInterval = setInterval(() => {
        if (this.live || !this.playing || this.currentTimeIsChanging) {
          return;
        }
        const { syncTimestamp, notSyncedCams } = this.checkAllVideosTimeSync();
        if (notSyncedCams.length > 0) {
          if (syncTimestamp) {
            this.handleChangeCurrentTime({
              startTimestamp: syncTimestamp,
              live: false,
              controller: VideoControllersType.DragSeekbar,
            });
          } else {
            console.warn('[WARN] syncing time is ', syncTimestamp);
          }
        }
      }, interval);
    },
    handleVideoStreamTimeUpdate(timestamp, camId) {
      this.videoTimeBox[camId] = timestamp;
      if (this.firstConnectedCameraId !== camId) {
        return;
      }

      if (this.currentTimeIsChanging) {
        return;
      }

      this.timestamp = timestamp;
      this.detectActiveEvent(timestamp);
      this.dateTimeNow = dayjs().valueOf();
      this.$emit('onTimestampChange', timestamp);
    },
    detectActiveEvent(timestamp) {
      if (this.videoSeekEventList.length) {
        const timestampDayjs = dayjs(timestamp);

        const activeEvents =
          this.videoSeekEventList.filter((event) => {
            const isAfterStart = timestampDayjs.isAfter(dayjs(event.start));
            const isBeforeEnd = timestampDayjs.isBefore(dayjs(event.end));
            const isActive = isAfterStart && isBeforeEnd;

            return isActive;
          }) || null;

        const eventsChanged = !isEqual(this.prevActiveEvents, activeEvents);

        if (eventsChanged) {
          this.prevActiveEvents = activeEvents;
          this.setActiveEvents(activeEvents);
        }
      }
    },
    handleChangeCurrentTime({
      startTimestamp,
      endTimestamp,
      live,
      pauseAfterLoad,
      direction,
      value,
      unit,
      startPosition,
      controller,
    }) {
      this.currentTimeIsChanging = true;
      // reset sync VOD interval preventing from syncing video during the seekbar time change
      this.syncVOD();
      this.$emit('onTimestampChange', startTimestamp);

      const wasLive = this.previousLiveState;
      this.live = live;
      this.previousLiveState = live;

      if (wasLive && !live) {
        this.switchPlaybackModeAndLog(false);
      }

      this.setHomeSeekStart(startTimestamp);
      const payload = {
        startTimestamp,
        pauseAfterLoad,
        value,
        unit,
        controller,
        direction,
        startPosition,
        endTimestamp,
        live,
      };
      this.emitSeek(payload);
      this.currentTimeIsChanging = false;
    },
    /**
     * Emit a seek event to all video streams with a new timestamp
     */
    emitSeek(payload) {
      EventBus.emit(EventBusEvents.CallVideoStream, {
        type: 'seek',
        payload,
      });
    },
    handleGetDate(date) {
      // cancel video clip selection when calendar date is changed
      if (this.trimMode) {
        this.cancelEdit();
      }
      this.setHomeSeekStart(date);
      this.refetchHomeEvents({
        timestamp: date,
        timeRange: this.currentTimeRange,
        caller: CallerKeys.HomeVideo_handleGetDate,
      });
    },
    handleSetTimeRange(timeRange) {
      this.currentTimeRange = timeRange;
      this.$emit('onSetTimeRange', timeRange);
      if (!this.live) {
        this.refetchHomeEvents({
          timestamp: this.timestamp,
          timeRange: timeRange,
        });
      }
    },
    handleLive() {
      const isSwitchingFromVOD = !this.previousLiveState;
      if (isSwitchingFromVOD) {
        this.switchPlaybackModeAndLog(true);
      } else {
        this.logPlaybackForDisplayedCameras(true);
      }
      this.displayLive();
      this.setHomeSeekStart(null);
      this.$nextTick(() => {
        this.refetchHomeEvents({
          timestamp: dayjs().valueOf(),
          timeRange: this.currentTimeRange,
          shouldReset: true,
        });
      });
    },
    refetchHomeEvents({
      timestamp,
      endtime,
      timeRange,
      minRetentionDate,
      shouldReset,
      caller,
    } = {}) {
      // If the user is a guest and has only LIVE-ONLY permissions, skip fetching events.
      if (!this.canEditVideoSeekDateTime) {
        return;
      }
      EventBus.emit(EventBusEvents.CallHomeEvents, {
        type: 'fetch-events',
        payload: {
          timestamp,
          endtime,
          timeRange,
          minRetentionDate,
          shouldReset,
          caller,
        },
      });
    },
    handleSelectCamera({ selected, id }, location) {
      this.$set(this.camSelectItems, location, {
        selected,
        id,
      });
    },
    async handleTrimEvent(event) {
      const { cameraId, eventId } = event;
      const startTime =
        event.startEventUnix != null ? event.startEventUnix : dayjs().valueOf();
      const endTime =
        event.endEventUnix != null ? event.endEventUnix : dayjs().valueOf();
      const camsList = this.getCamsMetadata([cameraId]);
      if (camsList.length) {
        if (event.eventThumbnailUrl) {
          camsList[0].thumbnail = event.eventThumbnailUrl;
        }
        const videoClipsData = [];
        // create transition images and video clips data for each selected camera
        camsList.forEach((cam) => {
          const { id, name } = cam;
          // create payload for video clip data to send to server
          const videoClipData = {
            cameraId: id,
            name: getVideoClipName(name, startTime, endTime),
            eventId,
            tags: [],
          };
          videoClipsData.push(videoClipData);
        });
        this.playTransitionCams(camsList);
        // send video clips data to server
        await this.createVideoClip({ data: videoClipsData });
      } else {
        const name = this.cameraById[event.cameraId].name;
        await this.createVideoClip({
          data: [
            {
              cameraId,
              name: getVideoClipName(name, startTime, endTime),
              eventId,
              tags: [],
            },
          ],
        });
      }
      EventBus.emit(EventBusEvents.TrimEventComplete, this.selectedEvent);
    },
    handleTrimChange(trim) {
      this.trimStart = trim.start;
      this.trimEnd = trim.end;
      this.trimMovePoint = trim.movePoint;
      this.trimMinRange = trim.minRange;
      this.durationFactor = trim.durationFactor;
    },
    downloadDataURL(b64data, name) {
      // Construct the <a> element
      const link = document.createElement('a');
      link.download = name;
      // Construct the uri
      const uri = b64data;
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      // Cleanup the DOM
      document.body.removeChild(link);
    },
    toggleShoot() {
      if (this.captureMode) {
        this.handleCapture();
      } else if (this.trimMode) {
        this.handleTrim();
      }
    },
    getCamsMetadata(camIds) {
      const images = [];
      camIds.forEach((id, index) => {
        // get selected camera
        const camRef = this.$refs[`display-cam${id}`];
        const selectedCam = Array.isArray(camRef) ? camRef[0] : camRef;

        // for cameras not shown in dashboard
        if (!selectedCam) {
          return [];
        }

        // get main camera
        // const mainCamRef = this.$refs[`display-cam${this.mainCams[0]}`];
        // const mainCam = Array.isArray(mainCamRef) ? mainCamRef[0] : mainCamRef;
        // const mainCamVideoMetadata = mainCam.videoMetadata;

        // get secondary cameras snapshot with main cam video width and height
        const canvas =
          this.mainCams[0] !== id
            ? selectedCam.snapOption({ h: 720, ratio: 1, w: 1280 })
            : selectedCam.snap();
        const { name } = this.cameraById[id];
        images.push({ canvas, index, id, name, ref: selectedCam });
      });
      return images;
    },
    handleCapture() {
      // Get selected/connected camera ids
      const selectedCamsId = Object.keys(this.camSelectItems).reduce(
        (acc, key) => {
          const item = this.camSelectItems[key];
          if (
            item &&
            item.selected &&
            this.cameraStatus[item.id] === CameraStatus.Connected
          ) {
            acc.push(item.id);
          }
          return acc;
        },
        []
      );
      this.getCamsMetadata(selectedCamsId).forEach((image, index) => {
        setTimeout(
          () => {
            // file name download image
            const fileName = `SECOM Sights-${image.name}-${dayjs().format(
              'YYYY-MM-DD_HH-mm-ss'
            )}.jpeg`;
            this.downloadDataURL(
              image.canvas.toDataURL('image/jpeg'),
              fileName
            );
            try {
              logEvent(HistoryCode.Snapshot, image.id);
            } catch (error) {
              console.error(
                `Failed to log snapshot for camera ${image.id}:`,
                error
              );
            }
          },
          this.isSafari ? index * 1000 : 0
        );
      });
    },
    getEventsInTrimSelector() {
      const eventsInTrimSelector = [];
      this.videoSeekEventList.forEach((event) => {
        const start = event.startUnix;
        const startTrim = this.trimStart;
        const endTrim = this.trimEnd;
        if (startTrim <= start && endTrim >= start) {
          eventsInTrimSelector.push(event);
        }
      });
      return eventsInTrimSelector;
    },
    async handleTrim(playSpeed = 1) {
      const selectedCamsIds = Object.keys(this.camSelectItems).reduce(
        (acc, key) => {
          const item = this.camSelectItems[key];
          if (
            item &&
            item.selected &&
            this.cameraStatus[item.id] === CameraStatus.Connected
          ) {
            acc.push(item.id);
          }
          return acc;
        },
        []
      );
      const camsList = this.getCamsMetadata(selectedCamsIds);
      const videoClipsData = [];

      // create transition images and video clips data for each selected camera
      const startDate = dayjs(this.trimStart).format('YYYYMMDDHHmmss');
      let endDate = dayjs(this.trimEnd).format('YYYYMMDDHHmmss');
      const maximumClipLength = 1800; // Clips must be <= 30 minutes
      if (
        dayjs(endDate).diff(dayjs(startDate), 'seconds') > maximumClipLength
      ) {
        endDate = dayjs(startDate)
          .add(maximumClipLength, 'seconds')
          .format('YYYYMMDDHHmmss');
      }
      if (this.trimMinRange) {
        let minTrimDuration = 1;
        switch (this.durationFactor) {
          case 3:
            minTrimDuration = 5;
            break;
          case 6:
            minTrimDuration = 10;
            break;
          case 12:
            minTrimDuration = 20;
            break;
          case 24:
            minTrimDuration = 30;
            break;
          case 1:
          default:
            minTrimDuration = 1;
            break;
        }
        endDate = dayjs(this.trimStart)
          .add(minTrimDuration, 'minutes')
          .format('YYYYMMDDHHmmss');
      }
      camsList.forEach((cam) => {
        const { id, name } = cam;
        // create payload for video clip data to be sent to server
        const videoClipData = {
          cameraId: id,
          name: getVideoClipName(name, startDate, endDate),
          startDate,
          endDate,
          tagIds: [],
          playSpeed,
        };

        videoClipsData.push(videoClipData);
      });

      this.playTransitionCams(camsList);

      // send video clips data to server
      await this.createVideoClip({ data: videoClipsData, startDate, endDate });
    },
    playTransitionCams(camList) {
      this.transitionImages = {};
      const trimMenuEl = document.getElementById('header-menu-trim-video');
      const targetClient = trimMenuEl.getBoundingClientRect();
      camList.forEach((cam) => {
        const { canvas, id, ref, thumbnail } = cam;

        const { width, height, x, y } = ref.$el.getBoundingClientRect();
        this.transitionImages = {
          ...this.transitionImages,
          [id]: {
            src: thumbnail ? thumbnail : canvas.toDataURL('image/jpg'),
            width,
            height,
            x,
            y,
            tx: targetClient.x - x + targetClient.width / 2,
            ty: targetClient.y - y,
            show: true,
          },
        };

        setTimeout(() => {
          this.transitionImages[id].show = false;
        }, 1000);
      });
    },
    toggleTrimMode(mode) {
      this.trimMode = mode !== undefined ? mode : !this.trimMode;
      if (this.trimMode) {
        this.captureMode = false;
      } else {
        this.trimStart = 0;
        this.trimEnd = 0;
        this.trimMovePoint = null;
      }
      this.initCamSelect();
    },
    toggleCaptureMode(mode) {
      this.captureMode = mode !== undefined ? mode : !this.captureMode;
      if (this.captureMode) {
        this.trimMode = false;
      }
      this.initCamSelect();
    },
    initCamSelect() {
      this.deselectAllCams();
      this.$nextTick(() => {
        this.selectFirstConnectedCamera();
      });
    },
    deselectAllCams() {
      Object.keys(this.camSelectItems).forEach((key) => {
        this.$set(this.camSelectItems[key], 'selected', false);
        this.$set(this.camSelectItems[key], 'id', this.allHomeCamsId[key]);
      });
    },
    selectFirstConnectedCamera() {
      // set first connected camera as a default selected camera
      Object.keys(this.camSelectItems).forEach((key) => {
        if (this.camSelectItems[key].id === this.firstConnectedCameraId) {
          this.$set(this.camSelectItems[key], 'selected', true);
        }
      });
    },
    cancelEdit() {
      this.toggleTrimMode(false);
      this.toggleCaptureMode(false);
    },
    refreshVideoStream() {
      if (this.live) {
        this.logPlaybackForDisplayedCameras(true);
        this.displayLive();
      } else {
        this.logPlaybackForDisplayedCameras(false);
        this.emitChangeFragmentTime({
          startTimestamp: this.timestamp,
          endTimestamp: this.endTimestamp,
          live: false,
        });
      }
    },
    handleDragEnd() {
      const draggingOverIndex = this.draggingOverIndex;
      const mainStay = this.movedFrom === 'main' && draggingOverIndex === 3;
      if (
        !this.movedFrom ||
        !this.movedTo ||
        draggingOverIndex < 0 ||
        this.movingIndex === draggingOverIndex ||
        mainStay
      ) {
        // Drop nowhere or same location, do nothing
        this.draggingOverIndex = null;
        return;
      }

      this.updateStartTimestampWithSeekbar();
      if (this.movedFrom === 'secondary' && this.movedTo === 'main') {
        const draggedElementValue = this.secondaryCams[this.movingIndex];
        const mainElementValue = this.mainCams[0];
        this.secondaryCams.splice(this.movingIndex, 1, mainElementValue);
        this.mainCams = [draggedElementValue];
      } else if (this.movedFrom === 'main' && this.movedTo === 'secondary') {
        const draggedElementValue = this.mainCams[0];
        const secondaryElementValue = this.secondaryCams[draggingOverIndex];
        this.mainCams = [secondaryElementValue];
        this.secondaryCams[draggingOverIndex] = draggedElementValue;
      } else if (
        this.movedFrom === 'secondary' &&
        this.movedTo === 'secondary'
      ) {
        const draggedElementValue = this.secondaryCams[this.movingIndex];
        const secondaryElementValue = this.secondaryCams[draggingOverIndex];
        this.secondaryCams[this.movingIndex] = secondaryElementValue;
        this.secondaryCams[draggingOverIndex] = draggedElementValue;
      }

      this.changeCurrentLiveMode();

      // create an initial layout array with the main and secondary cameras
      const layout = [
        this.mainCams[0],
        this.secondaryCams[0],
        this.secondaryCams[1],
      ];

      // get the active page indices from the pagination component
      const pageIndices = this.$refs.paginate.getActivePages();

      // map the layout to a sorted array with cameraId and position
      const sortedLayout = layout.filter(Boolean).map((cameraId, index) => ({
        cameraId,
        position: (() => {
          if (
            pageIndices[index]?.content == null ||
            pageIndices[index]?.content === undefined
          ) {
            throw new Error(`Invalid content at pageIndices[${index}]`);
          }
          return pageIndices[index].content;
        })(),
      }));

      // ensure all cameras in the current group are included in the sorted layout
      this.currentGroupCamList.forEach((camera, index) => {
        if (!sortedLayout.some((layout) => layout.cameraId === camera.id)) {
          sortedLayout.push({
            cameraId: camera.id,
            position: index + 1,
          });
        }
      });

      // sort the layout by position to ensure correct order
      sortedLayout.sort((a, b) => a.position - b.position);

      const payload = {
        cameraGroupId: this.currentHomeGroup,
        cameraDirectoryId: this.currentCameraDirectoryId,
        sort: sortedLayout,
      };

      this.updateCameraOrder(payload);

      this.movedFrom = '';
      this.movedTo = '';
      this.draggingOverIndex = null;
    },
    adaptCameraStatus() {
      for (const camId in this.cameraStatus) {
        if (!this.allHomeCamsId.includes(+camId)) {
          this.$delete(this.cameraStatus, +camId);
        }
      }
    },
    getCameraLiveMode() {
      // TODO: When enabling WebRTC, ensure compliance with the specifications.
      return 'HLS';
    },
    setDraggingOverIndex(index) {
      this.draggingOverIndex = index;
    },
    handleMove(e) {
      const { to, from, draggedContext } = e;
      if (to.className.indexOf('homeVideo_main') > -1) {
        this.movedTo = 'main';
      } else if (to.className.indexOf('homeVideo_secondaries') > -1) {
        this.movedTo = 'secondary';
      } else {
        this.movedTo = '';
      }
      if (from.className.indexOf('homeVideo_main') > -1) {
        this.movedFrom = 'main';
      } else if (from.className.indexOf('homeVideo_secondaries') > -1) {
        this.movedFrom = 'secondary';
      } else {
        this.movedFrom = '';
      }

      const { index, futureIndex } = draggedContext;
      this.movingIndex = index;
      this.futureIndex = futureIndex > 1 ? 1 : futureIndex;
      return false;
    },
    // handle main camera status e.g. connected, nodata, error
    handleCamStatus({ status, id, code }) {
      if (code) {
        return;
      }

      // Check whether any camera is connected at initializing time to set as video seek refrence for time update
      if (
        status === CameraStatus.Connected &&
        this.firstConnectedCameraId === null
      ) {
        this.firstConnectedCameraId = id;
        this.canAutoplay = true;
        this.changeCurrentLiveMode();
      }
      const cameraAvailabiliy = [
        CameraStatus.Connected,
        PlayerStatus.Discontinuity,
        KVSErrorCodes.NO_FRAGMENT_FOUND,
        PlayerStatus.OutOfRetentionPeriod,
      ];
      this.$set(this.cameraStatus, id, status);
      this.adaptCameraStatus();
      this.$nextTick(() => {
        // Check whether any camera is connected to set as video seek refrence for time update

        this.firstConnectedCameraId =
          [this.mainCams[0], this.secondaryCams[0], this.secondaryCams[1]].find(
            (connectedCamId) =>
              cameraAvailabiliy.includes(this.cameraStatus[connectedCamId])
          ) || null;

        if (this.firstConnectedCameraId) {
          this.changeCurrentLiveMode();
        } else {
          console.warn(
            '[WARN] No camera is connected or available for playback'
          );
        }

        this.isAnyCamConnected = Object.values(this.cameraStatus).some(
          (status) => cameraAvailabiliy.includes(status)
        );
        this.isAnyCamInGap = Object.values(this.cameraStatus).some(
          (status) =>
            status === PlayerStatus.Discontinuity ||
            status === KVSErrorCodes.NO_FRAGMENT_FOUND ||
            status === PlayerStatus.OutOfRetentionPeriod
        );

        this.handleWindowResize();
      });
    },
    // handle fullscreen change e.g. true, false
    handleFullscreenChange({ fullscreen }) {
      this.fullscreen = fullscreen;
    },
    handleEventChange({ cameraId }) {
      if (this.$refs.paginate) {
        const targetPage =
          this.currentGroupCamList.findIndex(
            (cam) => String(cam.id) === String(cameraId)
          ) + 1;
        const willPageChange = this.page !== targetPage;
        if (willPageChange) {
          this.pendingEventLogging = this.previousLiveState
            ? { isLive: false, wasLive: true }
            : false;
        }
        this.$refs.paginate.handlePageSelected(targetPage);
        return willPageChange;
      }
      return false;
    },
    setupLive() {
      const hasEventId = !!this.$route.query.eventid;
      const selectedEvent = this.$store.state.event.event;

      if (hasEventId && selectedEvent) {
        return;
      }

      this.playbackRate = 1;
      this.previousLiveState = this.live;
      this.live = true;
      this.startTimestamp = dayjs().subtract(1, 's').valueOf();
      this.endTimestamp = dayjs().valueOf();
    },
    async displayLive() {
      const hasEventId = !!this.$route.query.eventid;
      const selectedEvent = this.$store.state.event.event;

      if (hasEventId && selectedEvent) {
        return;
      }

      this.currentTimeIsChanging = true;
      this.$emit('onTimestampChange', dayjs().valueOf());
      this.setupLive();
      clearTimeout(window.timeoutdisplayLive);

      window.timeoutdisplayLive = setTimeout(() => {
        this.emitChangeFragmentTime({
          startTimestamp: dayjs().subtract(1, 's').valueOf(),
          endTimestamp: this.endTimestamp,
          live: true,
        });
        this.currentTimeIsChanging = false;
        this.playing = true;
      }, 1000);
    },
    async displayEvent(event, skipPageChange = false) {
      // check for duplicate call
      if (
        event &&
        this.lastDisplayedEventId &&
        String(event.eventId) === String(this.lastDisplayedEventId)
      ) {
        return;
      }

      if (event && event.eventId) {
        this.lastDisplayedEventId = String(event.eventId);
      }

      if (!event) {
        console.warn('Event is not available to display');
        return;
      }

      const eventKey = `${event.eventId}-${event.startEventUnix}`;
      if (
        this.displayEventInProgressKey === eventKey ||
        !event.startEventUnix
      ) {
        console.warn('Event does not have startEventUnix property:', event);
        return;
      }
      this.displayEventInProgressKey = eventKey;

      // Clear logged cameras when event changes so displayed cameras are logged again
      this.clearAllLoggedCameras();

      try {
        this.currentTimeIsChanging = true;

        this.startTimestamp = event.startEventUnix;
        this.timestampAfterPageUpdate = event.startEventUnix;
        const wasLive = this.previousLiveState;
        this.previousLiveState = false;
        this.live = false;

        // calculate end timestamp
        let endTimestamp = dayjs(event.startEventUnix)
          .add(22, 'hour')
          .valueOf();
        if (endTimestamp > dayjs().valueOf()) {
          endTimestamp = dayjs().subtract(1, 's').valueOf();
        }
        this.endTimestamp = endTimestamp;
        this.mountState = {};
        // skip page change if page was already set via initialPage
        let didPageChanged = false;
        if (!skipPageChange) {
          didPageChanged = this.handleEventChange(event);
        } else {
          // page was already set correctly, but verify it matches the event camera
          const targetPage =
            this.currentGroupCamList.findIndex(
              (cam) => String(cam.id) === String(event.cameraId)
            ) + 1;
          if (this.page !== targetPage) {
            // page does not match - this should not happen, but handle it
            console.warn(
              `Page mismatch: expected ${targetPage}, got ${this.page}`
            );
            didPageChanged = this.handleEventChange(event);
          }
        }

        // If the page does not change we should set `timestampAfterPageUpdate` to null
        if (!didPageChanged) {
          this.timestampAfterPageUpdate = null;
        }

        this.changeCameraLoading(false);

        clearTimeout(window.timeoutdisplayEvent);

        const changeTimeToEvent = () => {
          if (wasLive && !didPageChanged) {
            this.switchPlaybackModeAndLog(false);
          } else if (!wasLive && !didPageChanged) {
            // Already in VOD mode, no page change, but we cleared sets - log cameras
            this.logPlaybackForDisplayedCameras(false);
          }

          this.emitChangeFragmentTime({
            startTimestamp: event.startEventUnix,
            endTimestamp: this.endTimestamp,
            live: false,
            isEvent: true,
          });
          this.playing = true;
          this.currentTimeIsChanging = false;

          this.detectActiveEvent(getEventStartWithOffset(event.startEventUnix));
        };

        changeTimeToEvent();

        // end of event display processing
        this.eventDisplayCallSource = null;
      } finally {
        this.displayEventInProgressKey = null;
      }
    },
    handleWindowResize() {
      // Set height to prevent from overlapping content
      const homeVideosHeight = this.$refs.homeVideo?.$el.clientHeight || 0;
      if (homeVideosHeight) {
        let bottomHeight = 120;
        if (homeVideosHeight < 740) bottomHeight = 160;
        let maxWidth = Math.trunc(
          (this.$refs.homeVideo.$el.clientHeight - bottomHeight) * 2.2
        );
        if (maxWidth < 768) maxWidth = 768;
        this.homeVideosMaxWidth = { maxWidth: maxWidth + 'px' };
      }
      if (this.$refs.seekWrapper) {
        this.timelineWidth = this.$refs.seekWrapper.clientWidth;
      }
    },
    emitChangeFragmentTime(payload) {
      EventBus.emit(EventBusEvents.CallVideoStream, {
        type: 'changeFragmentTime',
        payload,
      });
    },
    ...mapActions('video', ['createVideoClip']),
    ...mapActions('home', [
      'updateCameraOrder',
      'setActiveEvents',
      'setTimeRange',
      'setHomeSeekStart',
      'setMaxRetentionPeriodDays',
      'fetchCameraLayout',
      'saveCameraLayout',
    ]),
  },
};
</script>

<style lang="scss">
.homeVideo {
  position: relative;
  overflow: hidden;
  .videoStream {
    .noThumbnailImage--absolute {
      height: calc(100% - 5px) !important;
    }
  }
  header {
    text-align: right;
  }
  &_nextcam {
    display: inline-grid;
    width: 20px;
    height: 80px;
    position: absolute;
    right: -20px;
    background: #fff;
    box-shadow: 0px 0px 4px -2px rgba(0, 0, 0, 0.75);
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    i.v-icon.v-icon {
      color: #0075c2;
    }
  }
  &_prevcam {
    display: inline-grid;
    width: 20px;
    height: 80px;
    position: absolute;
    left: -20px;
    background: #fff;
    box-shadow: 0px 0px 4px -2px rgba(0, 0, 0, 0.75);
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    i.v-icon.v-icon {
      transform: rotate(180deg);
      color: #0075c2;
    }
  }
  &_paginate {
    position: absolute;
    left: 0;
    top: 0px;
    width: 100%;
  }
  &_dialog {
    &-close {
      right: 3px;
      top: 3px;
      position: absolute;
      &.theme--light.v-btn.v-btn--icon {
        color: #999;
      }
    }
    &-error {
      position: fixed;
      text-align: center;
      top: 30px;
      left: 50%;
      z-index: 10;
      transform: translate(-50%, 0);
      width: 375px;
      height: 81px;
      padding: 20px;
      border-radius: 10px;
      box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
      background-color: rgba(0, 0, 0, 0.7);
      p {
        width: 335px;
        height: 41px;
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.5;
        letter-spacing: normal;
        text-align: center;
        color: #fff;
      }
    }
  }
  &_tools {
    margin-top: -13px;
  }
  &_layout-btn {
    &.theme--light.v-btn.v-btn--icon {
      font-size: 16px;
    }
  }
  &--loading.homeVideo {
    &.v-sheet {
      padding: 30px;
    }
  }
  &.v-sheet {
    padding: 0;
  }
  &_control-box {
    position: absolute;
    background-color: #fff;
    bottom: 0px;
    left: 0;
    width: 100%;
    padding: 7px 30px 30px 30px;
    z-index: 1;
    &--tools {
      max-height: 246px;
      padding: 4px 30px 7px 30px;
    }
    &--active {
      box-shadow: 0px -5px 7px -3px rgb(0 0 0 / 32%);
    }
    &-gap {
      min-height: 10px;
    }
  }
  &_trim {
    &-transition {
      position: absolute;
      z-index: 3;
    }
  }
  &_hide {
    visibility: hidden;
    & > * {
      visibility: hidden !important;
    }
  }
  &_drop {
    background-color: #f3f3f3;
  }
  &_videoStream {
    height: 100%;
    .videoControllers {
      z-index: 1;
      margin-top: -5px;
      padding-top: 5px;
      background-color: white;
    }
  }
  &_divider {
    height: 10px;
  }
  &_invisible {
    visibility: hidden !important;
    width: 0px !important;
    position: absolute !important;
  }
  &_no-data-cameras {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    background-color: #8c8c8a85;
  }
  display: flex;
  flex-direction: column;
  &_title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
    color: var(--text-color);
    padding: 5px 30px 0;

    max-width: 966px;
    @media (min-width: 1180px) {
      width: calc(68vw - 339.77px);
    }
    &--invisible {
      visibility: hidden;
    }
    &_text {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
      height: 10px;
      &-event {
        color: rgba(0, 0, 0, 0.4);
      }
    }
    &_type {
      font-size: 12px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: right;
      color: #4b4b4b;
      position: absolute;
      background-color: #fcc800;
      padding: 2px 7px;
      border-bottom-left-radius: 5px;
      top: 13px;
      right: 3px;
      z-index: 1;
    }
    &-trim {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
      text-align: center;
      z-index: 1;
      width: 127px;
      height: 22px;
      padding: 1px 5px;
      border-radius: 3px;
      border: solid 3px #37a34a;
      background-color: #37a34a;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 0.93;
      letter-spacing: normal;
      text-align: left;
      color: #fff;
      &--secondary {
        top: -10px;
      }
    }
  }
  &_videos {
    position: relative;
    padding: 0px 30px 30px;
    &-wrapper {
      @include scrollBar;
      overflow-x: hidden;
    }
    &--loading {
      aspect-ratio: 16/9;
      display: block !important;
      height: calc(100% - 100px);
      width: 100%;
    }
    @media (min-width: 1180px) {
      display: flex;
      justify-content: space-between;
      column-gap: 30px;
    }
  }
  &_main {
    position: relative;
    padding-top: 30px;
    .homeVideo_title-trim {
      top: 20px;
    }
    .homeVideo_title_type {
      top: 33px;
    }
    & * {
      outline: 0px solid transparent;
    }
    & *:focus {
      outline: 0px solid transparent;
    }
    @media (min-width: 1180px) {
      width: calc(68% - 0px);
    }
    &-event {
      .videoStream_video video {
        border: solid 3px var(--accent-color);
        height: calc(100% - 31px);
      }
    }
  }
  &_selected-capture {
    .videoStream_video {
      video {
        border: solid 3px #0075c2;
      }
    }
  }
  &_capture--bg-color {
    background-color: #0075c2;
    color: #fff;
  }
  &_selected-trim {
    .videoStream_video {
      video {
        border: solid 3px #37a34a;
      }
    }
  }
  &_trim--bg-color {
    background-color: #37a34a;
    color: #fff;
  }
  &_unselected {
    .videoStream_video {
      video {
        border: solid 3px #ccc;
      }
    }
  }
  &_unselected--bg-color {
    background-color: #ccc;
    color: #fff;
  }
  &_secondaries {
    display: flex;
    justify-content: space-between;
    margin: 33px 0 0;
    padding-top: 30px;
    @media (min-width: 1180px) {
      flex-direction: column;
      margin: 0;
      width: calc(32% - 18px);
      .videoStream_details {
        margin-bottom: calc(2vw - 25px);
      }
      .homeVideo_secondary {
        min-height: unset;
        width: 100%;
      }
    }
    @media (min-width: 1920px) {
      .videoStream_details {
        margin-bottom: 15px;
      }
    }
  }
  &_secondary {
    min-height: 100%;
    width: calc(50% - 15px);
    position: relative;
    & * {
      outline: 0px solid transparent;
    }
    & *:focus {
      outline: 0px solid transparent;
    }
    &-title {
      font-size: 12px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: right;
      color: #4b4b4b;
      position: absolute;
      background-color: #fcc800;
      padding: 2px 7px;
      border-bottom-left-radius: 5px;
      top: 3px;
      right: 3px;
      z-index: 1;
    }
  }
  &_separator {
    display: none;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.06) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    height: 10px;
    margin: -40px -30px 30px;
    position: absolute;
    width: 100%;
    top: 0;
  }
  &_edit {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 24px;
    align-items: baseline;

    &-right {
      display: flex;
      justify-content: space-between;
      width: 100%;
      .v-btn {
        width: 120px;
        font-size: 14px !important;
        font-weight: bold;
      }
    }
    &-camtext {
      font-size: 12px;
      font-weight: bold;
      line-height: 1.08;
      color: #989898;
      margin-right: 10px;
    }
    &-cambtn {
      margin-right: 10px;
    }
    &-shootbtn {
      margin-left: 10px;
      .icon {
        font-size: 32px;
      }
    }
    &-cancel {
      &.theme--light.v-btn.v-btn--has-bg {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: center;
        color: #989898;
        border-radius: 5px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        background-color: #fff;
      }
    }
    &-timelapse {
      &.theme--light.v-btn.v-btn--has-bg {
        font-size: 14px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: center;
        color: #0075c2;
        border-radius: 5px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        background-color: #fff;
        &.v-btn--disabled {
          box-shadow: none;
        }
      }
    }
    &-btn_gap {
      width: 120px;
      height: 40px;
    }
  }
}
</style>
